import { useEffect, useState } from 'react'
import { FaPen } from 'react-icons/fa'
import { Button, NativeSelect, Spinner, StatusPill } from '../../../ui'
import { formatDate } from '../commsInboxView/shared'
import { ExportGroupBy } from '../commsShared/exportDownload'
import { DeepDiveScope, Subcategory, SubcategoryBrief, SuggestedAction, deepDiveApi, messageOf } from '../commsShared/deepDive'
import { ReportMenu } from '../commsMailboxWorkspaceView/CategoryReport'
import styles from './styles.module.scss'

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-GB'))

export const TIMING_OPTIONS = [
    { value: '', label: 'No timing' },
    { value: 'now', label: 'Now' },
    { value: 'within_7_days', label: 'Within 7 days' },
    { value: 'within_14_days', label: 'Within 14 days' },
    { value: 'in_4_weeks', label: 'In 4 weeks' },
    { value: 'in_6_weeks', label: 'In 6 weeks' },
    { value: 'in_8_weeks', label: 'In 8 weeks' },
    { value: 'on_reply', label: 'When they reply' },
]

export const ACTION_TYPES = [
    { value: '', label: 'Kind of work…' },
    { value: 'reply_now', label: 'Reply to the sender' },
    { value: 'send_to_client', label: 'Ask the client for something' },
    { value: 'chase_later', label: 'Chase later' },
    { value: 'wait', label: 'Wait' },
    { value: 'escalate', label: 'Escalate' },
    { value: 'close', label: 'Close' },
    { value: 'other', label: 'Other' },
]

export const timingText = (t: string | null | undefined) => (t ? TIMING_OPTIONS.find((o) => o.value === t)?.label ?? t.replace(/_/g, ' ') : null)
export const kindText = (t: string | null | undefined) => (t ? ACTION_TYPES.find((o) => o.value === t)?.label ?? t.replace(/_/g, ' ') : null)

/** One line for the card / table: what the feature must deliver. */
export const requirementLine = (a: SuggestedAction | null | undefined) => a?.label || null

/** "Not built" until a feature is registered against the sub-category key. Nothing is registered yet. */
export const FeaturePill = ({ size = 'sm' }: { size?: 'sm' | 'md' }) => (
    <StatusPill size={size} tone="neutral" title="No feature does this work yet. The requirements a developer needs are in the sub-category's details.">
        Feature not built
    </StatusPill>
)

interface Props {
    scope: DeepDiveScope
    sub: Subcategory
    /** Every live sub-category, for the merge target list. */
    all: Subcategory[]
    saving: boolean
    exporting: boolean
    onEdit: () => void
    onMerge: (intoId: string) => void
    onExport: (groupBy: ExportGroupBy | null) => void
    onViewThreads: () => void
    onClose: () => void
}

/**
 * What one sub-category is, what its threads ask for, and what a feature for it
 * would have to do. Stats come from the list; the brief (common items, lenders,
 * example wording) is fetched when the panel opens.
 */
export const SubcategoryDetail = ({ scope, sub, all, saving, exporting, onEdit, onMerge, onExport, onViewThreads, onClose }: Props) => {
    const [brief, setBrief] = useState<SubcategoryBrief | null>(null)
    const [briefError, setBriefError] = useState<string | null>(null)
    const [merging, setMerging] = useState(false)

    useEffect(() => {
        let cancelled = false
        setBrief(null)
        setBriefError(null)
        deepDiveApi
            .brief(scope, sub.id)
            .then((b) => !cancelled && setBrief(b))
            .catch((e) => !cancelled && setBriefError(messageOf(e, 'Could not build the brief')))
        return () => {
            cancelled = true
        }
    }, [scope, sub.id, sub.threads])

    const a = sub.suggestedAction
    const others = all.filter((o) => o.id !== sub.id).map((o) => ({ value: o.id, label: o.label }))
    const kind = kindText(a?.type)
    const timing = timingText(a?.timing)

    return (
        <div className={styles.detail} onClick={(e) => e.stopPropagation()}>
            <div className={styles.detailHead}>
                <div className={styles.detailTitle}>
                    <h4>{sub.label}</h4>
                    <StatusPill size="sm" tone={sub.source === 'human' ? 'accent' : 'neutral'} title={sub.source === 'human' ? `Added by ${sub.createdBy ?? 'a person'}` : 'Proposed by the model'}>
                        {sub.source === 'human' ? 'ours' : 'model'}
                    </StatusPill>
                    <FeaturePill />
                    <span className={styles.mono}>{sub.key}</span>
                </div>
                <div className={styles.detailActions}>
                    <Button size="sm" variant="ghost" onClick={onViewThreads} title="Show the threads with this label and their quoted evidence">
                        View items
                    </Button>
                    <ReportMenu unit="thread" variant="ghost" exporting={exporting} onExport={onExport} label="Export" />
                    {others.length > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setMerging((v) => !v)} disabled={saving} title="Move every thread here into another sub-category and retire this one">
                            Merge…
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" icon={<FaPen aria-hidden />} onClick={onEdit} disabled={saving} title="Rename, describe, or change the requirement">
                        Edit
                    </Button>
                    <Button size="sm" variant="link" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>

            {merging && (
                <div className={styles.mergeRow}>
                    <span className={styles.muted}>Move all {fmt(sub.threads ?? 0)} threads into</span>
                    <NativeSelect
                        small
                        defaultValue=""
                        placeholder="Choose…"
                        options={others}
                        disabled={saving}
                        onChange={(e) => {
                            const into = all.find((o) => o.id === e.target.value)
                            if (into && window.confirm(`Move every thread from "${sub.label}" into "${into.label}" and retire "${sub.label}"?`)) onMerge(into.id)
                        }}
                    />
                    <Button size="sm" variant="link" onClick={() => setMerging(false)}>
                        Cancel
                    </Button>
                </div>
            )}

            {sub.description && <p className={styles.detailDesc}>{sub.description}</p>}

            <ul className={styles.detailStats}>
                <li>
                    <strong>{fmt(sub.threads ?? 0)}</strong> threads
                </li>
                <li>
                    <strong>{fmt(sub.linked ?? 0)}</strong> with a client or claim
                </li>
                {brief && (brief.totals.fromLender > 0 || brief.totals.fromClient > 0) && (
                    <li>
                        <strong>{fmt(brief.totals.fromLender)}</strong> from lenders · <strong>{fmt(brief.totals.fromClient)}</strong> from clients
                    </li>
                )}
                <li className={(sub.needsCheck ?? 0) > 0 ? styles.warn : undefined}>
                    <strong>{fmt(sub.needsCheck ?? 0)}</strong> need a check
                </li>
                <li className={(sub.flagged ?? 0) > 0 ? styles.bad : undefined}>
                    <strong>{fmt(sub.flagged ?? 0)}</strong> flagged
                </li>
                <li>
                    <strong>{fmt(sub.last7d ?? 0)}</strong> this week
                </li>
                {brief?.totals.firstAt && (
                    <li className={styles.muted}>
                        first seen {formatDate(brief.totals.firstAt)}
                        {brief.totals.lastAt ? `, latest ${formatDate(brief.totals.lastAt)}` : ''}
                    </li>
                )}
            </ul>

            <section className={styles.requirements} aria-label="Requirements for the feature">
                <h5>
                    What the feature has to do <span className={styles.muted}>· requirements gathered by the deep dive</span>
                </h5>
                <p className={styles.muted}>Nothing runs for this sub-category yet. A developer builds the feature from what follows; once it is registered against the key above, it replaces this section.</p>

                <dl className={styles.reqGrid}>
                    <dt>Outcome</dt>
                    <dd>{a?.label ? <strong>{a.label}</strong> : <span className={styles.muted}>not stated - edit to add</span>}</dd>
                    {a?.description && (
                        <>
                            <dt>How</dt>
                            <dd>{a.description}</dd>
                        </>
                    )}
                    {(kind || timing) && (
                        <>
                            <dt>Kind · when</dt>
                            <dd>
                                {kind ?? '—'}
                                {timing ? ` · ${timing.toLowerCase()}` : ''}
                            </dd>
                        </>
                    )}
                </dl>

                {briefError && <p className={styles.bad}>{briefError}</p>}
                {!brief && !briefError && <Spinner size="sm" text="Reading the threads…" />}
                {brief && (
                    <div className={styles.reqCols}>
                        {brief.items.length > 0 && (
                            <div>
                                <h6>What is asked for, most often first</h6>
                                <ul className={styles.reqList}>
                                    {brief.items.map((it) => (
                                        <li key={it.detail}>
                                            <span>{it.detail}</span>
                                            <span className={styles.muted}>{fmt(it.threads)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {brief.lenders.length > 0 && (
                            <div>
                                <h6>Who sends it</h6>
                                <ul className={styles.reqList}>
                                    {brief.lenders.map((l, i) => (
                                        <li key={`${l.name ?? 'unknown'}-${i}`}>
                                            <span>{l.name ?? <span className={styles.muted}>unknown</span>}</span>
                                            <span className={styles.muted}>{fmt(l.threads)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {brief.quotes.length > 0 && (
                            <div className={styles.reqQuotes}>
                                <h6>How they word it</h6>
                                {brief.quotes.map((q, i) => (
                                    <blockquote key={i} className={`${styles.quote} ${styles.quoteVerified}`}>
                                        <span className={styles.quoteMark} aria-hidden>
                                            “
                                        </span>
                                        {q.quote}
                                        <span className={styles.quoteMark} aria-hidden>
                                            ”
                                        </span>
                                        {q.detail && <span className={styles.quoteDetail}> — {q.detail}</span>}
                                        {q.threads > 1 && <span className={styles.quoteDetail}> · in {fmt(q.threads)} threads</span>}
                                    </blockquote>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    )
}
